import { TSavedLaneSchema } from 'transfix-form/forms/SavedLaneForm/schema'
import { IDateTypes, IEquipmentTypes, IRateTypes } from 'transfix-form/forms/SavedLaneForm/types'
import { getIsoDays, parseLocation } from 'transfix-form/forms/SavedLaneForm/utils'
import {
  BookableShipmentSavedSearch,
  CreateBookableShipmentSavedSearchRequestBody,
} from 'apps/fleet-planner/types'

/**
 * Returns a BookableShipmentSavedSearch rateType value based on IRateTypes enum value.
 * If rateType is IRateTypes.anyRate then return null
 *
 * @example getRateType(IRateTypes.ratePerMile) // 'rpm'
 * @example getRateType(IRateTypes.totalAllIn) // 'all_in'
 * @example getRateType(IRateTypes.anyRate) // null
 */
const getRateType = (rateType: IRateTypes): BookableShipmentSavedSearch['rateType'] => {
  if (rateType === IRateTypes.ratePerMile || rateType === IRateTypes.totalAllIn) {
    return rateType
  }
  return null
}

/**
 * Generates a BookableShipmentSavedSearch object
 * from a TSavedLaneSchema object
 */
export const getBookableShipmentSavedSearchRequestBody = (
  savedLane: TSavedLaneSchema
): CreateBookableShipmentSavedSearchRequestBody => {
  const {
    date,
    dateType,
    daysOfWeek,
    equipment,
    lane,
    motorCarrierId,
    rate,
    rateType: savedLaneRateType,
    receiveNotifications,
  } = savedLane || {}

  const { dropoff, dropoffRadius, pickup, pickupRadius } = lane || {}
  const { text: pickupText } = pickup || {}
  const { text: dropoffText } = dropoff || {}
  const { city: pickupCity, stateCode: pickupStateCode } = parseLocation(pickupText) || {}
  const { city: deliveryCity = null, stateCode: deliveryStateCode = null } =
    parseLocation(dropoffText) || {}

  return {
    bookableShipmentSavedSearch: {
      pickupCity,
      pickupStateCode,
      pickupMiles: pickupRadius,
      pickupStartDate: dateType === IDateTypes.specificDates ? date : null,
      pickupIsoDaysOfWeek:
        dateType === IDateTypes.daysOfWeek && daysOfWeek ? getIsoDays(daysOfWeek) : null,
      deliveryCity,
      deliveryStateCode,
      deliveryMiles: deliveryCity && deliveryStateCode ? dropoffRadius : null,
      trailerType: equipment === IEquipmentTypes.anyEquipment ? null : equipment,
      rate: rate || null,
      rateType: getRateType(savedLaneRateType),
      motorCarrierId: Number(motorCarrierId),
      hasNotificationsEnabled: receiveNotifications,
      sourceType: 'dispatcher_created',
    },
  }
}
